import { Donut } from "@react-gcc-eds/charts";
import { Serie } from "@react-gcc-eds/charts/dist/components/donut/interfaces";
import { Pill, Tile, useSettings } from "@react-gcc-eds/core";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { useMetricsFiltersActions } from "../../../../actions/metrics-filters-actions";
import { useSettingsActions } from "../../../../actions/settings-actions";
import { useStore } from "../../../../store";
import { dateAsYMDString } from "../../../../utils";
import { useLocalization } from "../../../../utils/localization";
import DatePicker from "../../../filters/date-picker";
import { usePredefinedRanges } from "../../../filters/date-picker/predefined-ranges";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";
import { usePoStatus } from "../../helpers/po-status";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { NoMetrics, noStatusMetric } from "../utils";

const OverallInternalOrderStatusDonut = ({
  customerId,
  className,
  alignment,
  onItemSelected
}: {
  customerId: string;
  className: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const { currentCustomer } = useSettingsActions();
  const [hiddenOrdersStatuses, setHiddenOrdersStatuses] = useState([]);
  const tileRef = useRef<HTMLDivElement>(null);
  const [state] = useStore();
  const { localeString } = useLocalization();
  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);
  const { getInternalOrderCreationDateDonutMetric } = useMetricsActions();
  const { setMetricsOverallInternalOrderStatusDonutCreationRange } = useMetricsFiltersActions();
  const { internalLabelToDeliveryStatus } = usePoStatus();
  const { rangeAsString, largestRange, isLargestRange } = usePredefinedRanges(
    new Date(currentCustomer().dataAvailableFromDate)
  );
  const { theme } = useSettings();

  useEffect(() => {
    if (state.metricsFilters.overallInternalOrderStatusDonutCreationRange) {
      const { from, to } = state.metricsFilters.overallInternalOrderStatusDonutCreationRange;
      getInternalOrderCreationDateDonutMetric(dateAsYMDString(from), dateAsYMDString(to));
    }
  }, [state.metricsFilters.overallInternalOrderStatusDonutCreationRange, customerId]);

  const purchaseOrders = () => {
    const { internalOrderCreationDateDonutMetric } = state.metrics;
    const { internalOrderCreationDateDonutMetric: loading } = state.fetchingData.metrics;

    if (loading) {
      return <NoMetrics loading />;
    }
    if (noStatusMetric(internalOrderCreationDateDonutMetric)) {
      return <NoMetrics />;
    }

    const d: Serie[] = [];
    internalOrderCreationDateDonutMetric.forEach(m => {
      d.push({
        colorID: m.status,
        name: m.label,
        values: [m.totalCount],
        hidden: hiddenOrdersStatuses.find(i => i === m.status) !== undefined
      });
    });

    return (
      <Donut
        onClick={(label: string) => {
          if (onItemSelected) {
            onItemSelected({
              filters: {
                orderCreationRange:
                  state.metricsFilters.overallInternalOrderStatusDonutCreationRange,
                status: internalLabelToDeliveryStatus(label)
              },
              graph: DashboardGraph.InternalOrders
            });
          }
        }}
        data={{ series: d }}
        unit="orders"
        showValue
        showAbsoluteValue
        onToggle={(data: any) =>
          setHiddenOrdersStatuses(
            data.series.filter((d: Serie) => d.hidden).map((d: Serie) => d.colorID)
          )
        }
      />
    );
  };

  const resetFilter = () => {
    setMetricsOverallInternalOrderStatusDonutCreationRange(largestRange.selector());
  };

  const resetFilterPill = () => {
    return (
      <Pill className={cx("filter-pill", theme)} onToggle={resetFilter}>
        Reset filters
      </Pill>
    );
  };

  const filtersSummary = (
    <div className="dashboard-active-filter">
      <Pill>
        {rangeAsString(state.metricsFilters.overallInternalOrderStatusDonutCreationRange)}
      </Pill>
      {!isLargestRange(state.metricsFilters.overallInternalOrderStatusDonutCreationRange)
        ? resetFilterPill()
        : null}
    </div>
  );

  const dateFilter = (
    <DatePicker
      key="datepicker"
      dual={state.userPreferences.useDualCalendar}
      rolling
      right={alignment !== TileAlignment.LEFT}
      selectedRange={state.metricsFilters.overallInternalOrderStatusDonutCreationRange}
      oldestDate={new Date(currentCustomer().dataAvailableFromDate)}
      onRangeSelected={setMetricsOverallInternalOrderStatusDonutCreationRange}
    />
  );

  const tileHeader = (
    <div className="dashboard-tile">
      <DashboardTooltipHeader
        header={
          localeString("Dashboard_InternalOverallOrderStatusPieChart") ||
          "Overall Internal Order Satus"
        }
        tooltipInfo="Graph is showing the status for the selected time period"
        alignment={alignment}
      />
    </div>
  );

  return (
    <>
      <Tile
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
        subtitle={filtersSummary}
        actions={[dateFilter]}
      >
        {purchaseOrders()}
      </Tile>
    </>
  );
};

export default OverallInternalOrderStatusDonut;
